import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Sparkle, Copy, PenNib, Question } from "@phosphor-icons/react";
import { toast } from "sonner";

function parseJson(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch {}
  const m = raw.match(/[\[{][\s\S]*[\]}]/);
  if (m) { try { return JSON.parse(m[0]); } catch {} }
  return null;
}

export default function ContentStudio() {
  const { activeBiz } = useOutletContext();
  const [tab, setTab] = useState("post");
  const [busy, setBusy] = useState(false);
  const [post, setPost] = useState(null);
  const [qa, setQa] = useState(null);
  const [postRaw, setPostRaw] = useState("");
  const [qaRaw, setQaRaw] = useState("");

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  const generatePost = async () => {
    setBusy(true); setPost(null);
    try {
      const { data } = await api.post(`/businesses/${activeBiz.id}/ai/post`);
      setPostRaw(data.raw);
      const p = parseJson(data.raw);
      if (p) setPost(p); else toast.error("AI returned non-JSON, see raw output.");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const generateQA = async () => {
    setBusy(true); setQa(null);
    try {
      const { data } = await api.post(`/businesses/${activeBiz.id}/ai/qa`);
      setQaRaw(data.raw);
      const p = parseJson(data.raw);
      if (Array.isArray(p)) setQa(p); else toast.error("AI returned non-JSON, see raw output.");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-5xl">
      <header>
        <div className="label-tiny mb-1">AI Content Studio</div>
        <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="content-title">Drafts that rank</h1>
        <p className="text-zinc-500 mt-1">Read-only mode: copy & paste into your GBP. Auto-publishing unlocks once Google API approval lands.</p>
      </header>

      <div className="flex gap-px bg-zinc-200 border border-zinc-200 w-fit" data-testid="content-tabs">
        {[
          { id: "post", label: "GBP Post", Icon: PenNib },
          { id: "qa", label: "Q&A", Icon: Question },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-5 py-2.5 text-xs uppercase tracking-widest font-bold flex items-center gap-2 ${tab === t.id ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`}
            data-testid={`content-tab-${t.id}`}
          >
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "post" && (
        <div className="space-y-4">
          <button className="btn-primary" onClick={generatePost} disabled={busy} data-testid="generate-post">
            {busy ? <span className="spinner"></span> : <><Sparkle size={14} weight="fill" /> Generate Post</>}
          </button>
          {post && (
            <div className="bento p-6 space-y-4" data-testid="post-card">
              <div>
                <div className="label-tiny">Title</div>
                <div className="font-display text-2xl font-bold mt-1">{post.title}</div>
              </div>
              <div>
                <div className="label-tiny">Body</div>
                <p className="font-mono text-sm leading-relaxed mt-2 whitespace-pre-wrap">{post.body}</p>
              </div>
              {post.cta && (
                <div><div className="label-tiny">CTA</div><div className="text-sm mt-1 font-semibold">{post.cta}</div></div>
              )}
              {post.suggested_image_prompt && (
                <div><div className="label-tiny">Image prompt suggestion</div><div className="text-xs font-mono text-zinc-600 mt-1">{post.suggested_image_prompt}</div></div>
              )}
              <button className="btn-secondary" onClick={() => copy(`${post.title}\n\n${post.body}\n\n${post.cta || ""}`)} data-testid="copy-post">
                <Copy size={14} weight="bold" /> Copy
              </button>
            </div>
          )}
          {!post && postRaw && <pre className="bento p-4 font-mono text-xs whitespace-pre-wrap">{postRaw}</pre>}
        </div>
      )}

      {tab === "qa" && (
        <div className="space-y-4">
          <button className="btn-primary" onClick={generateQA} disabled={busy} data-testid="generate-qa">
            {busy ? <span className="spinner"></span> : <><Sparkle size={14} weight="fill" /> Generate Q&As</>}
          </button>
          {qa && (
            <div className="space-y-3" data-testid="qa-list">
              {qa.map((item, i) => (
                <div key={i} className="bento p-5">
                  <div className="font-bold mb-2">{item.question}</div>
                  <p className="font-mono text-sm leading-relaxed text-zinc-700">{item.answer}</p>
                  <button className="btn-secondary mt-3 !py-2 !text-[11px]" onClick={() => copy(`Q: ${item.question}\nA: ${item.answer}`)}>
                    <Copy size={12} /> Copy
                  </button>
                </div>
              ))}
            </div>
          )}
          {!qa && qaRaw && <pre className="bento p-4 font-mono text-xs whitespace-pre-wrap">{qaRaw}</pre>}
        </div>
      )}
    </div>
  );
}
